import { useEffect, useState } from "react";
import { Send, Eye, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { RoastSponsors } from "@/components/admin/RoastSponsors";
import { WelcomePanel } from "@/components/admin/WelcomePanel";

const RunsTable = ({ runs, testid }) =>
  runs.length === 0 ? <p className="text-sm text-[#575757] mt-2">Nothing yet.</p> : (
    <table className="w-full text-sm mt-3" data-testid={testid}><tbody>
      {runs.map((r) => (
        <tr key={r.run_id} className="border-t border-[#E7E7E7] align-top">
          <td className="py-2 pr-2 whitespace-nowrap">{fmtDate(r.created_at)}</td><td className="pr-2 capitalize">{r.trigger}</td>
          <td className={`pr-2 ${r.status === "failed" ? "text-[#E5332A]" : ""}`}>{r.status}</td>
          <td className="text-xs text-[#575757]">{r.title || r.error || ""}</td>
        </tr>))}
    </tbody></table>
  );

export const AdminNewsletter = () => {
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailToo, setEmailToo] = useState(false);
  const load = () => api.get("/admin/newsletter").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const writing = d?.runs?.[0]?.status === "writing";
  useEffect(() => { if (!writing) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [writing]);

  const showPreview = async () => setPreview((await api.get("/admin/newsletter/preview", { responseType: "text" })).data);
  const writeNow = async () => {
    try { await api.post("/admin/roast", { email: emailToo }); toast.success("Writing this week's Roast. It takes 2 to 3 minutes."); setTimeout(load, 1500); } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendNow = async () => {
    if (!window.confirm(`Email the latest Tuesday Roast to ${d.subscribers} subscribers now?`)) return;
    setBusy(true);
    try { await api.post("/admin/newsletter/send"); toast.success("Sending started"); setTimeout(load, 4000); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!d) return <p className="overline text-[#575757]">Loading…</p>;

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-newsletter">
      <div className="lg:col-span-5 space-y-6">
        <div className="bg-[#0A0A0A] text-white p-6">
          <p className="overline text-[#E5332A]">The Tuesday Roast</p>
          <p className="font-display text-3xl font-bold mt-2" data-testid="newsletter-subscriber-count">{d.subscribers} {d.subscribers === 1 ? "subscriber" : "subscribers"}</p>
          <p className="text-sm text-white/70 mt-2">Written, published and emailed automatically every Tuesday at 5:00 (UK time): the week's big stories, Africa's deals with global markets and the commodity price board.</p>
          {d.latest && <Link to={`/article/${d.latest.slug}`} data-testid="roast-latest-link" className="block text-sm mt-4 underline underline-offset-2">Latest: {d.latest.title} · {fmtDate(d.latest.published_at)}</Link>}
          <div className="flex flex-wrap gap-2 mt-6">
            <button onClick={writeNow} disabled={writing} data-testid="roast-write-now-btn" className="flex items-center gap-2 bg-white text-[#0A0A0A] px-4 py-2 text-sm hover:bg-[#E7E7E7] disabled:opacity-50"><PenLine size={14} /> {writing ? "Writing…" : "Write this week's Roast now"}</button>
            <button onClick={showPreview} data-testid="newsletter-preview-btn" className="flex items-center gap-2 border border-white/30 px-4 py-2 text-sm hover:bg-white/10"><Eye size={14} /> Preview email</button>
            <button onClick={sendNow} disabled={busy || !d.subscribers || !d.latest} data-testid="newsletter-send-now-btn" className="flex items-center gap-2 bg-[#E5332A] px-4 py-2 text-sm hover:bg-[#B8241C] disabled:opacity-50"><Send size={14} /> Email latest now</button>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 mt-4"><input type="checkbox" data-testid="roast-email-checkbox" checked={emailToo} onChange={(e) => setEmailToo(e.target.checked)} /> Also email subscribers when the new Roast is ready</label>
        </div>
        <div className="bg-white border border-[#E7E7E7] p-6"><p className="font-display text-lg font-bold">Writing runs</p><RunsTable runs={d.runs} testid="roast-runs-table" /></div>
        <RoastSponsors />
        <WelcomePanel />
        <div className="bg-white border border-[#E7E7E7] p-6">
          <p className="font-display text-lg font-bold">Sent emails</p>
          {d.issues.length === 0 ? <p className="text-sm text-[#575757] mt-2">No issues sent yet.</p> : (
            <table className="w-full text-sm mt-3" data-testid="newsletter-issues-table"><tbody>
              {d.issues.map((i) => (
                <tr key={i.run_id} className="border-t border-[#E7E7E7]">
                  <td className="py-2">{fmtDate(i.created_at)}</td><td className="capitalize">{i.trigger}</td>
                  <td>{i.sent}/{i.recipients} sent</td><td className={i.status === "sent" ? "" : "text-[#E5332A]"}>{i.status}</td>
                </tr>))}
            </tbody></table>
          )}
        </div>
      </div>
      <div className="lg:col-span-7 border border-[#E7E7E7] bg-[#F4F4F4] min-h-[400px]">
        {preview ? <iframe title="Newsletter preview" data-testid="newsletter-preview-frame" srcDoc={preview} className="w-full h-[760px] bg-white" sandbox="" />
          : <p className="p-8 text-sm text-[#575757]">Click Preview email to see the latest Tuesday Roast as subscribers receive it.</p>}
      </div>
    </div>
  );
};

export const AdminMonthly = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/monthly-report/runs").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const writing = d?.runs?.[0]?.status === "writing";
  useEffect(() => { if (!writing) return undefined; const t = setInterval(load, 10000); return () => clearInterval(t); }, [writing]);
  const writeNow = async () => {
    try { await api.post("/admin/monthly-report"); toast.success("Writing this month's report. It takes 4 to 8 minutes."); setTimeout(load, 1500); } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendTeaser = async () => {
    if (!window.confirm("Email the teaser for the latest report to all free subscribers now?")) return;
    try { await api.post("/admin/monthly-report/teaser"); toast.success("Teaser sending started"); setTimeout(load, 4000); } catch (e) { toast.error(formatApiError(e)); }
  };
  const shareLi = async () => {
    try {
      const { data } = await api.post(`/admin/linkedin/share/${d.latest.slug}`);
      toast.success(data.status === "already_posted" ? "This report is already on LinkedIn" : "Posted to LinkedIn");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!d) return <p className="overline text-[#575757]">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-monthly">
      <div className="lg:col-span-5 bg-[#0A0A0A] text-white p-6">
        <p className="overline text-[#E5332A]">Monthly Africa Brew Brief Report</p>
        <p className="text-sm text-white/70 mt-3">Researched, written and published automatically on the 27th of each month at 7:00 (UK time). £49 per copy, included with Pro and Institutional.</p>
        {d.latest && <Link to={`/research/${d.latest.slug}`} data-testid="monthly-latest-link" className="block text-sm mt-4 underline underline-offset-2">Latest: {d.latest.title} · {d.latest.pages} pages</Link>}
        {d.latest && <button onClick={shareLi} data-testid="monthly-linkedin-share-btn" className="mt-3 text-xs border border-white/30 px-3 py-1.5 hover:bg-white/10">Share latest report to LinkedIn</button>}
        <button onClick={writeNow} disabled={writing} data-testid="monthly-write-now-btn" className="mt-6 flex items-center gap-2 bg-[#E5332A] px-4 py-2 text-sm hover:bg-[#B8241C] disabled:opacity-50"><PenLine size={14} /> {writing ? "Researching and writing…" : "Write this month's report now"}</button>
        <p className="text-xs text-white/50 mt-3">Writing again in the same month replaces that month's report.</p>
        <div className="border-t border-white/15 mt-6 pt-5">
          <p className="text-sm font-bold">Teaser email to free subscribers</p>
          <p className="text-xs text-white/60 mt-1">Sent automatically when each report is published: summary, three findings and a £49 buy button. Pro and Institutional readers don't get it.</p>
          {d.teaser ? <p className="text-xs mt-3" data-testid="monthly-teaser-status">Teaser {d.teaser.status}: {d.teaser.sent ?? 0} of {d.teaser.recipients ?? 0} sent · {fmtDate(d.teaser.created_at)}</p>
            : <button onClick={sendTeaser} disabled={!d.latest} data-testid="monthly-teaser-send-btn" className="mt-3 flex items-center gap-2 border border-white/30 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-50"><Send size={14} /> Email teaser now</button>}
        </div>
      </div>
      <div className="lg:col-span-7 bg-white border border-[#E7E7E7] p-6"><p className="font-display text-lg font-bold">Report runs</p><RunsTable runs={d.runs} testid="monthly-runs-table" /></div>
    </div>
  );
};
